/**
 * BillDetective — spend attribution
 *
 * The killer feature: tells customers *WHO* spent money and *on WHAT*,
 * not just *how much*. Three views:
 *   1. Engineer Leaderboard — per-engineer net $ impact (commits that
 *      added cost vs commits that saved cost)
 *   2. Code → Cost — top bill lines drilled down to the commit/PR/author
 *      that introduced them
 *   3. Per-feature & Per-customer cost — $/endpoint + $/customer rollups
 *
 * Demo accounts get deterministic mock data (so the dashboard is stable
 * across reloads). Real customers populate via GitHub webhook +
 * OTel hooks → /api/bill-detective/track-attribution and /track-feature.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Users, GitCommit, GitPullRequest, TrendingUp, TrendingDown,
  ArrowLeft, ArrowRight, Trophy, AlertTriangle, Activity,
  DollarSign, Code2, Layers, Building2, ExternalLink, Sparkles, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import BillDetectiveDigestToggle from '../components/BillDetectiveDigestToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtUsd = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}K`;
  if (abs >= 100)  return `${sign}$${abs.toFixed(0)}`;
  return `${sign}$${abs.toFixed(2)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtPct = (n) => {
  const v = Number(n || 0);
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};
const teamColor = (team) => {
  const map = {
    infra:    'text-amber-300 bg-amber-500/10 ring-amber-500/30',
    backend:  'text-sky-300 bg-sky-500/10 ring-sky-500/30',
    platform: 'text-violet-300 bg-violet-500/10 ring-violet-500/30',
    ml:       'text-rose-300 bg-rose-500/10 ring-rose-500/30',
    data:     'text-cyan-300 bg-cyan-500/10 ring-cyan-500/30',
    growth:   'text-emerald-300 bg-emerald-500/10 ring-emerald-500/30',
  };
  return map[team] || 'text-slate-300 bg-slate-500/10 ring-slate-500/30';
};

export default function BillDetective() {
  const [period, setPeriod] = useState('7d');
  const [leaderboard, setLeaderboard] = useState(null);
  const [costLines, setCostLines] = useState(null);
  const [features, setFeatures] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillLine, setDrillLine] = useState(null);
  const [drillData, setDrillData] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [lb, cl, ft, cu, sm] = await Promise.all([
        axios.get(`${API}/bill-detective/leaderboard?period=${period}`, auth()),
        axios.get(`${API}/bill-detective/cost-lines`, auth()),
        axios.get(`${API}/bill-detective/feature-costs?period=30d`, auth()),
        axios.get(`${API}/bill-detective/customer-costs?limit=10`, auth()),
        axios.get(`${API}/bill-detective/summary`, auth()),
      ]);
      setLeaderboard(lb.data);
      setCostLines(cl.data);
      setFeatures(ft.data);
      setCustomers(cu.data);
      setSummary(sm.data);
    } catch (e) {
      toast.error('Failed to load Bill Detective — please try again');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [period]);

  const openDrill = async (line) => {
    setDrillLine(line);
    setDrillData(null);
    try {
      const r = await axios.get(
        `${API}/bill-detective/cost-lines/${line.line_id}`, auth(),
      );
      setDrillData(r.data);
    } catch (e) {
      toast.error('Could not load drill-down');
    }
  };

  const isDemo = leaderboard?.data_source === 'mock_demo';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead title="Bill Detective — Who spent money? · CARTIEAI" description="Bill Detective attributes every cloud dollar to the team, the feature, and the git commit that caused it. When your bill jumps, you get a name — not a mystery." ogSlug="bill-detective" />

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* ── Top bar ── */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-slate-400 hover:text-slate-100"
            data-testid="bd-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
          </Link>
          <Badge
            className="bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/15"
            data-testid="bd-engine-badge"
          >
            <Sparkles className="w-3 h-3 mr-1" /> Bill Detective Engine — NEW
          </Badge>
        </div>

        {/* ── Hero ── */}
        <div className="mb-8" data-testid="bd-hero">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
            <span className="text-emerald-300">Bill Detective</span>
            <span className="text-slate-100"> — who spent the money?</span>
          </h1>
          <p className="text-lg text-slate-400 mt-4 max-w-3xl">
            Every other FinOps tool says <span className="text-slate-200 font-mono">&ldquo;$3,847 last week.&rdquo;</span>{' '}
            CARTIE is the only tool that names the engineer, the commit, the PR
            and the line of infra that caused it — so cost is finally
            <span className="text-emerald-300"> accountable</span>.
          </p>
          {isDemo && (
            <div className="mt-3 text-xs text-amber-400/80" data-testid="bd-demo-notice">
              Showing deterministic demo data. Connect your GitHub +
              cloud account to see your real team here.
            </div>
          )}
        </div>

        {/* ── Summary chiclets ── */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="bd-summary-grid">
            <SummaryTile
              testid="bd-tile-top-saver"
              icon={<Trophy className="w-4 h-4 text-emerald-300" />}
              label="Top saver (7d)"
              primary={summary.top_saver_name || '—'}
              secondary={summary.top_saver_name
                ? `saved ${fmtUsd(summary.top_saver_saved_usd)}/mo`
                : 'No savings yet'}
            />
            <SummaryTile
              testid="bd-tile-top-spender"
              icon={<AlertTriangle className="w-4 h-4 text-amber-300" />}
              label="Top spender (7d)"
              primary={summary.top_spender_name || '—'}
              secondary={summary.top_spender_name
                ? `added ${fmtUsd(summary.top_spender_spent_usd)}/mo`
                : 'No additions yet'}
            />
            <SummaryTile
              testid="bd-tile-commits"
              icon={<GitCommit className="w-4 h-4 text-sky-300" />}
              label="Commits scanned"
              primary={fmtInt(summary.total_commits_7d)}
              secondary={`across ${summary.engineer_count} engineers`}
            />
            <SummaryTile
              testid="bd-tile-net"
              icon={<DollarSign className="w-4 h-4 text-violet-300" />}
              label="Net $ impact (7d)"
              primary={fmtUsd(summary.net_usd_monthly_7d)}
              secondary={summary.net_usd_monthly_7d >= 0 ? 'added per month' : 'saved per month'}
              tone={summary.net_usd_monthly_7d < 0 ? 'emerald' : 'amber'}
            />
          </div>
        )}

        {/* ── Tabs ── */}
        <Tabs defaultValue="leaderboard" className="mb-8">
          <TabsList className="bg-slate-900/60 ring-1 ring-slate-800" data-testid="bd-tabs">
            <TabsTrigger value="leaderboard" data-testid="bd-tab-leaderboard">
              <Users className="w-4 h-4 mr-2" /> Engineer Leaderboard
            </TabsTrigger>
            <TabsTrigger value="code-to-cost" data-testid="bd-tab-code-to-cost">
              <Code2 className="w-4 h-4 mr-2" /> Code → Cost
            </TabsTrigger>
            <TabsTrigger value="features" data-testid="bd-tab-features">
              <Layers className="w-4 h-4 mr-2" /> Per-Feature
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="bd-tab-customers">
              <Building2 className="w-4 h-4 mr-2" /> Per-Customer
            </TabsTrigger>
          </TabsList>

          {/* ── Tab 1: Engineer Leaderboard ── */}
          <TabsContent value="leaderboard" className="mt-6" data-testid="bd-panel-leaderboard">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Net cost impact per engineer</h2>
              <div className="flex gap-2">
                {['7d', '30d', '90d'].map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={period === p ? 'default' : 'outline'}
                    onClick={() => setPeriod(p)}
                    data-testid={`bd-period-${p}`}
                    className={period === p
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-emerald-950'
                      : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>

            {loading || !leaderboard ? (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <CardContent className="p-12 text-center text-slate-500">Loading…</CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <div className="overflow-x-auto" data-testid="bd-leaderboard-table">
                  <table className="w-full text-sm">
                    <thead className="text-slate-400 border-b border-slate-800">
                      <tr>
                        <th className="text-left p-4 font-medium">#</th>
                        <th className="text-left p-4 font-medium">Engineer</th>
                        <th className="text-left p-4 font-medium">Team</th>
                        <th className="text-right p-4 font-medium">Commits</th>
                        <th className="text-right p-4 font-medium">Cost added</th>
                        <th className="text-right p-4 font-medium">Cost saved</th>
                        <th className="text-right p-4 font-medium">Net $/mo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.engineers.map((eng) => (
                        <tr key={eng.author_email}
                            className="border-b border-slate-800/60 hover:bg-slate-800/30"
                            data-testid={`bd-row-${eng.author_email}`}>
                          <td className="p-4 text-slate-500 font-mono">{eng.rank}</td>
                          <td className="p-4">
                            <div className="font-medium text-slate-100">{eng.author_name}</div>
                            <div className="text-xs text-slate-500">{eng.author_email}</div>
                          </td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full ring-1 ${teamColor(eng.author_team)}`}>
                              {eng.author_team || '—'}
                            </span>
                          </td>
                          <td className="p-4 text-right text-slate-300">{eng.commits_count}</td>
                          <td className="p-4 text-right text-amber-300">{fmtUsd(eng.added_usd_monthly)}</td>
                          <td className="p-4 text-right text-emerald-300">{fmtUsd(eng.saved_usd_monthly)}</td>
                          <td className="p-4 text-right">
                            <div className={`font-mono font-medium ${eng.net_impact_usd_monthly < 0 ? 'text-emerald-300' : 'text-amber-300'}`}>
                              {eng.net_impact_usd_monthly < 0 ? '−' : '+'}{fmtUsd(Math.abs(eng.net_impact_usd_monthly))}
                            </div>
                            {eng.badge === 'saver' && (
                              <span className="text-[10px] text-emerald-400/80">★ saver</span>
                            )}
                            {eng.badge === 'high_spender' && (
                              <span className="text-[10px] text-amber-400/80">⚠ high spender</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {leaderboard.engineers.length === 0 && (
                        <tr>
                          <td colSpan={7} className="p-12 text-center text-slate-500">
                            No engineer activity in the last {period}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="p-4 border-t border-slate-800 flex flex-wrap gap-6 text-xs text-slate-400">
                  <div>Total added: <span className="text-amber-300 font-mono">{fmtUsd(leaderboard.total_added_usd_monthly)}/mo</span></div>
                  <div>Total saved: <span className="text-emerald-300 font-mono">{fmtUsd(leaderboard.total_saved_usd_monthly)}/mo</span></div>
                  <div>Net: <span className={leaderboard.net_usd_monthly < 0 ? 'text-emerald-300 font-mono' : 'text-amber-300 font-mono'}>
                    {leaderboard.net_usd_monthly < 0 ? '−' : '+'}{fmtUsd(Math.abs(leaderboard.net_usd_monthly))}/mo
                  </span></div>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab 2: Code → Cost ── */}
          <TabsContent value="code-to-cost" className="mt-6" data-testid="bd-panel-code-to-cost">
            <div className="mb-4">
              <h2 className="text-lg font-medium">Bill lines linked to commits</h2>
              <p className="text-sm text-slate-400 mt-1">
                Click any line to see the exact commit + PR + author that made you pay for it.
              </p>
            </div>

            {loading || !costLines ? (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <CardContent className="p-12 text-center text-slate-500">Loading…</CardContent>
              </Card>
            ) : (
              <div className="grid gap-3" data-testid="bd-cost-lines">
                {costLines.lines.map((line) => (
                  <Card
                    key={line.line_id}
                    className="bg-slate-900/40 ring-1 ring-slate-800 hover:ring-emerald-500/40 cursor-pointer transition"
                    onClick={() => openDrill(line)}
                    data-testid={`bd-line-${line.line_id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex-1 min-w-[220px]">
                          <div className="text-base font-medium text-slate-100">{line.service_label}</div>
                          <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                            <span>{line.commits_in_line} commits</span>
                            <span>·</span>
                            <span className={line.trend_pct_30d >= 0 ? 'text-amber-300' : 'text-emerald-300'}>
                              {line.trend_pct_30d >= 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> : <TrendingDown className="w-3 h-3 inline mr-0.5" />}
                              {fmtPct(line.trend_pct_30d)} 30d
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="text-2xl font-mono text-slate-100">{fmtUsd(line.monthly_cost_usd)}<span className="text-xs text-slate-500">/mo</span></div>
                            <div className="text-xs text-slate-500">monthly run rate</div>
                          </div>
                          <div className="text-right border-l border-slate-800 pl-6">
                            <div className="text-xs text-slate-500">attributed to</div>
                            <div className="text-sm text-slate-200">{line.attributed_author}</div>
                            <div className="text-xs text-emerald-300 font-mono flex items-center gap-1">
                              <GitCommit className="w-3 h-3" />
                              {line.attributed_commit} · PR #{line.attributed_pr}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {costLines.lines.length === 0 && (
                  <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                    <CardContent className="p-12 text-center text-slate-500">
                      No bill lines yet. Once your GitHub webhook fires, top cost lines will appear here.
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── Tab 3: Per-Feature ── */}
          <TabsContent value="features" className="mt-6" data-testid="bd-panel-features">
            <div className="mb-4">
              <h2 className="text-lg font-medium">Per-endpoint cost telemetry</h2>
              <p className="text-sm text-slate-400 mt-1">
                What does each route really cost to serve? Sorted by total $.
              </p>
            </div>

            {loading || !features ? (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <CardContent className="p-12 text-center text-slate-500">Loading…</CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <div className="overflow-x-auto" data-testid="bd-features-table">
                  <table className="w-full text-sm">
                    <thead className="text-slate-400 border-b border-slate-800">
                      <tr>
                        <th className="text-left p-4 font-medium">#</th>
                        <th className="text-left p-4 font-medium">Endpoint</th>
                        <th className="text-right p-4 font-medium">Calls (30d)</th>
                        <th className="text-right p-4 font-medium">Compute $</th>
                        <th className="text-right p-4 font-medium">Token $</th>
                        <th className="text-right p-4 font-medium">Total $</th>
                        <th className="text-right p-4 font-medium">$ / 1K calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.features.map((f) => (
                        <tr key={f.endpoint} className="border-b border-slate-800/60 hover:bg-slate-800/30"
                            data-testid={`bd-feature-${f.endpoint}`}>
                          <td className="p-4 text-slate-500 font-mono">{f.rank}</td>
                          <td className="p-4">
                            <span className="text-xs font-mono text-slate-400 mr-2">{f.method}</span>
                            <span className="text-slate-200 font-mono">{f.endpoint}</span>
                          </td>
                          <td className="p-4 text-right text-slate-300">{fmtInt(f.calls_count)}</td>
                          <td className="p-4 text-right text-sky-300 font-mono">{fmtUsd(f.total_compute_usd)}</td>
                          <td className="p-4 text-right text-violet-300 font-mono">{fmtUsd(f.total_token_usd)}</td>
                          <td className="p-4 text-right text-slate-100 font-mono font-medium">{fmtUsd(f.total_usd)}</td>
                          <td className="p-4 text-right text-slate-400 font-mono">${(f.cost_per_1k_calls || 0).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="p-4 border-t border-slate-800 flex flex-wrap gap-6 text-xs text-slate-400">
                  <div>Total calls (30d): <span className="text-slate-200 font-mono">{fmtInt(features.total_calls)}</span></div>
                  <div>Total cost: <span className="text-slate-100 font-mono">{fmtUsd(features.total_usd)}</span></div>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab 4: Per-Customer ── */}
          <TabsContent value="customers" className="mt-6" data-testid="bd-panel-customers">
            <div className="mb-4">
              <h2 className="text-lg font-medium">Top customers by cost-to-revenue</h2>
              <p className="text-sm text-slate-400 mt-1">
                Flags unprofitable customers (cost &gt; revenue) so you can renegotiate or sunset.
              </p>
            </div>

            {loading || !customers ? (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <CardContent className="p-12 text-center text-slate-500">Loading…</CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-900/40 ring-1 ring-slate-800">
                <div className="p-4 border-b border-slate-800 text-xs text-slate-400">
                  {customers.unprofitable_count > 0 ? (
                    <span><AlertTriangle className="w-3 h-3 inline text-rose-400 mr-1" />
                      <span className="text-rose-300 font-medium">{customers.unprofitable_count}</span> customer{customers.unprofitable_count > 1 ? 's' : ''} cost more than they pay you
                    </span>
                  ) : (
                    <span className="text-emerald-300">All listed customers are profitable.</span>
                  )}
                </div>
                <div className="overflow-x-auto" data-testid="bd-customers-table">
                  <table className="w-full text-sm">
                    <thead className="text-slate-400 border-b border-slate-800">
                      <tr>
                        <th className="text-left p-4 font-medium">Customer</th>
                        <th className="text-left p-4 font-medium">Plan</th>
                        <th className="text-right p-4 font-medium">MRR</th>
                        <th className="text-right p-4 font-medium">Cost / mo</th>
                        <th className="text-right p-4 font-medium">Margin</th>
                        <th className="text-right p-4 font-medium">Calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.customers.map((c) => (
                        <tr key={c.customer_id}
                            className={`border-b border-slate-800/60 ${c.is_unprofitable ? 'bg-rose-500/5' : ''}`}
                            data-testid={`bd-cust-${c.customer_id}`}>
                          <td className="p-4">
                            <div className="text-slate-100">{c.customer_name}</div>
                            <div className="text-xs text-slate-500 font-mono">{c.customer_id}</div>
                          </td>
                          <td className="p-4 text-slate-300">{c.plan}</td>
                          <td className="p-4 text-right text-emerald-300 font-mono">{fmtUsd(c.mrr_usd)}</td>
                          <td className="p-4 text-right text-amber-300 font-mono">{fmtUsd(c.monthly_cost_usd)}</td>
                          <td className={`p-4 text-right font-mono ${c.margin_pct >= 0 ? 'text-slate-200' : 'text-rose-300'}`}>
                            {fmtPct(c.margin_pct)}
                          </td>
                          <td className="p-4 text-right text-slate-400 font-mono">{fmtInt(c.calls_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* ── Slack weekly digest opt-in (engagement multiplier) ── */}
        <BillDetectiveDigestToggle />

        {/* ── How-it-works strip ── */}
        <Card className="bg-slate-900/40 ring-1 ring-slate-800 mt-12" data-testid="bd-how-it-works">
          <CardContent className="p-6">
            <h3 className="text-sm font-medium text-slate-200 mb-3 flex items-center">
              <Search className="w-4 h-4 mr-2 text-emerald-300" /> How Bill Detective populates this
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-400">
              <div>
                <div className="text-xs text-emerald-300 mb-1">1. GitHub webhook</div>
                Each merged PR is parsed for cost-impacting Terraform / IaC changes. Detected impact is written to <span className="font-mono text-slate-300">bill_attributions</span>.
              </div>
              <div>
                <div className="text-xs text-emerald-300 mb-1">2. OTel hooks (per-endpoint cost)</div>
                The LLM proxy and our request middleware emit spans tagged with <span className="font-mono text-slate-300">endpoint</span>, <span className="font-mono text-slate-300">customer_id</span>, <span className="font-mono text-slate-300">compute_usd</span>.
              </div>
              <div>
                <div className="text-xs text-emerald-300 mb-1">3. Cloud bill reconciliation</div>
                We reconcile attributed impact against the actual AWS / Azure / GCP bill nightly — so reported numbers track what you really paid.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Code → Cost drill-down dialog ── */}
      <Dialog open={!!drillLine} onOpenChange={(o) => { if (!o) { setDrillLine(null); setDrillData(null); } }}>
        <DialogContent className="bg-slate-900 ring-1 ring-slate-800 text-slate-100 max-w-2xl" data-testid="bd-drill-dialog">
          {drillLine && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Code2 className="w-5 h-5 text-emerald-300" />
                  {drillLine.service_label} — <span className="font-mono">{fmtUsd(drillLine.monthly_cost_usd)}/mo</span>
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Who introduced this cost line?
                </DialogDescription>
              </DialogHeader>

              {!drillData ? (
                <div className="py-8 text-center text-slate-500">Loading…</div>
              ) : (
                <div className="space-y-4">
                  {drillData.primary_attribution && (
                    <Card className="bg-emerald-500/5 ring-1 ring-emerald-500/30">
                      <CardContent className="p-5">
                        <div className="text-xs text-emerald-300 mb-2">Primary attribution</div>
                        <div className="text-base font-medium text-slate-100">
                          {drillData.primary_attribution.author_name}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {drillData.primary_attribution.author_email} · team {drillData.primary_attribution.author_team}
                        </div>
                        <div className="mt-3 text-sm text-slate-200 italic">
                          &ldquo;{drillData.primary_attribution.commit_message}&rdquo;
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-400 font-mono">
                          <span className="bg-slate-800 px-2 py-1 rounded">
                            <GitCommit className="w-3 h-3 inline mr-1" />
                            {drillData.primary_attribution.commit_sha_short}
                          </span>
                          <a href={drillData.primary_attribution.pr_url}
                             target="_blank" rel="noopener noreferrer"
                             className="bg-slate-800 px-2 py-1 rounded hover:bg-slate-700 inline-flex items-center"
                             data-testid="bd-drill-pr-link">
                            <GitPullRequest className="w-3 h-3 inline mr-1" />
                            PR #{drillData.primary_attribution.pr_number}
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </a>
                          <span className="text-amber-300">
                            +{fmtUsd(drillData.primary_attribution.infra_impact_usd_monthly)}/mo
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {drillData.related_attributions && drillData.related_attributions.length > 1 && (
                    <div>
                      <div className="text-xs text-slate-400 mb-2">
                        {drillData.related_count - 1} other commit{drillData.related_count - 1 > 1 ? 's' : ''} touched this resource type
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {drillData.related_attributions.slice(1, 10).map((a, i) => (
                          <div key={i} className="text-xs bg-slate-800/50 ring-1 ring-slate-800 rounded p-3"
                               data-testid={`bd-drill-related-${i}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-slate-200">{a.author_name}</span>
                                <span className="text-slate-500 ml-2 font-mono">{a.commit_sha_short}</span>
                              </div>
                              <span className={a.cost_direction === 'added' ? 'text-amber-300 font-mono' : 'text-emerald-300 font-mono'}>
                                {a.cost_direction === 'added' ? '+' : '−'}{fmtUsd(Math.abs(a.infra_impact_usd_monthly))}/mo
                              </span>
                            </div>
                            <div className="text-slate-400 italic mt-1 truncate">&ldquo;{a.commit_message}&rdquo;</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setDrillLine(null); setDrillData(null); }}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="bd-drill-close"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ icon, label, primary, secondary, tone = 'slate', testid }) {
  const toneClass = tone === 'emerald'
    ? 'ring-emerald-500/30 bg-emerald-500/5'
    : tone === 'amber'
      ? 'ring-amber-500/30 bg-amber-500/5'
      : 'ring-slate-800 bg-slate-900/40';
  return (
    <Card className={`ring-1 ${toneClass}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          {icon}
          <span>{label}</span>
        </div>
        <div className="mt-2 text-xl font-medium text-slate-100">{primary}</div>
        <div className="text-xs text-slate-500 mt-1">{secondary}</div>
      </CardContent>
    </Card>
  );
}
